import React from 'react';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Assert } from '@cotera/utilities';
import { useUntenantedClient } from '@cotera/client/app/stores/org';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Icon } from '@cotera/client/app/components';
import { TimeInterval, TIME_INTERVAL_OPTIONS } from '@cotera/api';
import { OverviewTab } from './atc-visibility/tabs/overview-tab';
import { AtcWorkflowTab } from './atc-visibility/tabs/atc-workflow-tab';
import { SagaWorkflowTab } from './atc-visibility/tabs/saga-workflow-tab';
import { GroupedSnapshotTab } from './atc-visibility/tabs/grouped-snapshot-tab';
import { WebhookTriggersTab } from './atc-visibility/tabs/webhook-triggers-tab';
import { GenericServiceTab } from './atc-visibility/tabs/generic-service-tab';
import { CustomRestateQuery } from './atc-visibility/custom-restate-query';
import { atcQueryKeys } from './atc-visibility/query-keys';

// `WorkflowWebhookTrigger` is promoted to a standing top-level tab (see below),
// so keep it out of the dynamic "Other" dropdown alongside the other services
// that have their own tabs.
const KNOWN_SERVICES = new Set([
  'AtcWorkflow',
  'SagaWorkflow',
  'WorkflowWebhookTrigger',
]);

const DashboardContent: React.FC<{
  timeInterval: TimeInterval;
}> = ({ timeInterval }) => {
  const client = useUntenantedClient();

  const { data: statusData } = useSuspenseQuery({
    queryFn: async () => {
      const res = await client.atcVisibility.getStatusSummary({ timeInterval });
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.statusSummary(timeInterval),
  });

  const dynamicServices = React.useMemo(
    () =>
      [...new Set(statusData.map((s) => s.service))].filter(
        (name) => !KNOWN_SERVICES.has(name)
      ),
    [statusData]
  );

  const [activeTab, setActiveTab] = React.useState('overview');

  // Secondary tabs tucked under the "Other" dropdown. Everything not promoted
  // to a top-level tab (Other Sagas + any dynamic per-service tabs).
  const otherTabs = React.useMemo(
    () => [
      { value: 'saga-workflow', label: 'Other Sagas' },
      ...dynamicServices.map((service) => ({
        value: `service-${service}`,
        label: service,
      })),
    ],
    [dynamicServices]
  );

  const activeOtherTab = otherTabs.find((t) => t.value === activeTab);

  return (
    <Tabs
      value={activeTab}
      onValueChange={setActiveTab}
      className="flex flex-col flex-1 min-h-0"
    >
      <TabsList>
        <TabsTrigger value="overview">Overview</TabsTrigger>
        <TabsTrigger value="atc-workflow">ATC Workflow</TabsTrigger>
        <TabsTrigger value="grouped-snapshot">Grouped Snapshots</TabsTrigger>
        <TabsTrigger value="webhook-triggers">Webhook Triggers</TabsTrigger>
        <DropdownMenu>
          <DropdownMenuTrigger
            className={`inline-flex h-[calc(100%-1px)] items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap transition-[color,box-shadow] cursor-pointer focus-visible:outline-none ${
              activeOtherTab !== undefined
                ? 'bg-background shadow-sm text-foreground'
                : 'text-foreground'
            }`}
          >
            {activeOtherTab !== undefined ? activeOtherTab.label : 'Other'}
            <Icon icon="chevron-down" className="w-4 h-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {otherTabs.map((tab) => (
              <DropdownMenuItem
                key={tab.value}
                onSelect={() => setActiveTab(tab.value)}
              >
                {tab.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TabsList>

      <div className="flex-1 overflow-y-auto space-y-6 mt-4">
        <Card>
          <CardContent>
            <TabsContent value="overview">
              <OverviewTab timeInterval={timeInterval} />
            </TabsContent>

            <TabsContent value="atc-workflow">
              <AtcWorkflowTab timeInterval={timeInterval} />
            </TabsContent>

            <TabsContent value="grouped-snapshot">
              <GroupedSnapshotTab timeInterval={timeInterval} />
            </TabsContent>

            <TabsContent value="webhook-triggers">
              <WebhookTriggersTab timeInterval={timeInterval} />
            </TabsContent>

            <TabsContent value="saga-workflow">
              <SagaWorkflowTab timeInterval={timeInterval} />
            </TabsContent>

            {dynamicServices.map((service) => (
              <TabsContent key={service} value={`service-${service}`}>
                <GenericServiceTab
                  serviceName={service}
                  timeInterval={timeInterval}
                />
              </TabsContent>
            ))}
          </CardContent>
        </Card>

        <CustomRestateQuery />
      </div>
    </Tabs>
  );
};

export const AtcVisibilityPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [timeInterval, setTimeInterval] = React.useState<TimeInterval>('2h');

  const refetchAll = () => {
    void queryClient.invalidateQueries({
      queryKey: atcQueryKeys.all,
      refetchType: 'all',
    });
  };

  return (
    <div className="flex flex-col h-full p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">ATC Visibility</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Monitoring data from Restate and Postgres
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={timeInterval}
            onValueChange={(value) => setTimeInterval(value as TimeInterval)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              {TIME_INTERVAL_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={refetchAll}>Refresh All</Button>
        </div>
      </div>

      <React.Suspense
        fallback={<div className="h-12 rounded-lg bg-muted animate-pulse" />}
      >
        <DashboardContent timeInterval={timeInterval} />
      </React.Suspense>
    </div>
  );
};
